import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './LoginRegister.css';

const LoginRegister = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { register, loginWithToken, isAuthenticated } = useAuth();
  
  // Get state passed from the "Login to Read Online" button
  const fromPath = location.state?.from || searchParams.get('redirect') || '/products';
  const autoReadOnline = location.state?.autoReadOnline || searchParams.get('autoReadOnline') === 'true';

  const handleMagicLinkLogin = useCallback(async (token) => {
    setIsLoading(true);
    setError('');
    try {
      const result = await loginWithToken(token);
      console.log('Login result:', result);
      setMessage('Logged in successfully! Redirecting...');
      
      // Check for redirect URL in the magic link or query params
      const redirectUrl = searchParams.get('redirect') || fromPath;
      // Pass autoReadOnline state to the redirect
      setTimeout(() => navigate(redirectUrl, { state: { autoReadOnline } }), 1500);
    } catch (err) {
      console.error('Login error:', err.response?.data || err.message);
      const errorMsg = err.response?.data?.error || 'Invalid or expired login link. Please try again.';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [loginWithToken, navigate, searchParams, fromPath, autoReadOnline]);

  useEffect(() => {
    // Check for magic link token in URL
    const token = searchParams.get('token');
    if (token) {
      handleMagicLinkLogin(token);
    }
  }, [searchParams, handleMagicLinkLogin]);

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate(fromPath, { state: { autoReadOnline } });
    }
  }, [isAuthenticated, navigate, fromPath, autoReadOnline]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      // Get redirect URL to pass to backend for magic link
      // Include autoReadOnline in the redirect URL for the magic link
      const redirectUrl = autoReadOnline ? `${fromPath}?autoReadOnline=true` : fromPath;
      console.log('Registering with email:', email, 'redirect:', redirectUrl);
      const result = await register(email, redirectUrl);
      console.log('Register result:', result);
      
      if (result.email_sent) {
        // Email was sent - user must click the link in their email
        setMessage('Check your email for a login link!');
      } else if (result.auto_logged_in) {
        // Auto-logged in (production email bypass)
        setMessage('Welcome! Redirecting...');
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 500);
      } else if (result.debug_login_url) {
        // Email not configured - use debug link (dev only)
        // Append redirect param to debug URL
        const debugUrl = result.debug_login_url + (result.debug_login_url.includes('?') ? '&' : '?') + `redirect=${encodeURIComponent(redirectUrl)}`;
        setMessage('Email not available. Redirecting...');
        console.log('Debug login URL:', debugUrl);
        setTimeout(() => {
          window.location.href = debugUrl;
        }, 500);
      } else {
        setMessage(result.message || 'Account ready!');
      }
    } catch (err) {
      console.error('Register error:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>Welcome</h1>
        <p className="login-subtitle">Enter your email to sign in or create an account</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={isLoading}
            />
          </div>

          <button type="submit" className="login-btn" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Magic Link'}
          </button>
        </form>

        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}

        <p className="login-info">
          We'll send you a secure login link. No password needed!
        </p>
      </div>
    </div>
  );
};

export default LoginRegister;
